import { inject } from 'inversify';

import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { t } from '@/locales';
import { Logger } from '@/services/Logger';
import { ProQaTripReqMsg } from '@/services/signalr/MessageTypes/ProQa';
import { Signalr } from '@/services/signalr/Signalr';
import { Toaster } from '@/services/toaster/Toaster';
import { ProQAScriptStore } from '@/stores/ProQAScriptStore';
import { useStore as proQaUseStore } from '@/stores/ProQaStore';
import { IProQAScript } from '~/api/IProQAScript';
import { IDispatchMessage } from '~/api/messages/IDispatchMessage';
import { IProQaRunStatusRespMsg } from '~/api/messages/IProQaRunStatusRespMsg';
import { IProQaTripRespMsg } from '~/api/messages/IProQaTripRespMsg';

@SignalrListener
@service(ProQAScriptManager)
export class ProQAScriptManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(ProQAScriptStore) protected proQaScriptStore: ProQAScriptStore;
  @inject(Toaster) protected toaster: Toaster;

  async load(tripId: string) {
    const response = await this.signalr.invoke(ProQaTripReqMsg, { tripId });
    return response;
  }

  find(tripId: string) {
    return this.proQaScriptStore.find(tripId);
  }

  update(script: IProQAScript) {
    this.proQaScriptStore.update(script);
  }

  @SignalrMessage('ProQaTripMsg')
  async onProQaTripMsg(scriptResponse: IProQaTripRespMsg & IDispatchMessage) {
    if (scriptResponse) {
      this.proQaScriptStore.update({
        tripId: scriptResponse.tripId,
        determinantCode: scriptResponse.determinantCode,
        responderScript: scriptResponse.responderScript,
      });
    }
  }

  @SignalrMessage('ProQaRunStatusRespMsg')
  async onProQaRunStatusRespMsg(proQaStatus: IProQaRunStatusRespMsg & IDispatchMessage) {
    const proQaStore = proQaUseStore();
    const { status } = proQaStatus;
    proQaStore.isProQaConnected = status;
    if (status) {
      this.toaster.dismiss(proQaStore.errorToast);
      this.toaster.success(t('proQaMsg.connected.toast'));
      proQaStore.errorToast = undefined;
    }
    else {
      if (proQaStore.errorToast) {
        return;
      }
      proQaStore.errorToast = this.toaster.connection(t('proQaMsg.disconnected.toast'));
    }
  }
}
